// src/components/ProjectManagement/ProjectBrowserModal.jsx
import React, { useState, useEffect } from 'react';

const ProjectBrowserModal = ({ isOpen, onClose, onSelectProject, currentProjectId, projects }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [displayProjects, setDisplayProjects] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Load recent projects from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentProjects');
    if (stored) {
      try {
        const recent = JSON.parse(stored);
        setRecentProjects(recent.slice(0, 6));
      } catch (e) {
        console.error('Error loading recent projects:', e);
      }
    }
  }, []);

  // Sort projects by creation date (newest first) and then filter
  useEffect(() => {
    let filtered = [...projects];
    
    // Sort by createdAt date - NEWEST FIRST
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB - dateA; // Newest first
    });
    
    // Apply status filter
    if (selectedFilter === 'active') {
      filtered = filtered.filter(p => p.status === 'active');
    } else if (selectedFilter === 'completed') {
      filtered = filtered.filter(p => p.status === 'completed');
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(p => 
        p.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setDisplayProjects(filtered);
    setCurrentPage(1);
  }, [projects, searchTerm, selectedFilter]);

  // Pagination
  const totalProjects = displayProjects.length;
  const totalPages = Math.ceil(totalProjects / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = displayProjects.slice(startIndex, endIndex);

  const saveRecentProject = (project) => {
    const recent = [{ 
      id: project.id, 
      name: project.projectName, 
      progress: project.progress, 
      timestamp: Date.now() 
    }, ...(recentProjects || [])];
    const unique = recent.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
    const limited = unique.slice(0, 6);
    setRecentProjects(limited);
    localStorage.setItem('recentProjects', JSON.stringify(limited));
  };

  const handleSelectProject = (project) => {
    saveRecentProject(project);
    onSelectProject(project);
    onClose();
  };

  const getStatusColor = (status) => {
    if (status === 'active') return '#10b981';
    if (status === 'completed') return '#3b82f6';
    return '#f59e0b';
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay project-browser-overlay" onClick={onClose}>
      <div className="project-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="project-browser-header">
          <div className="project-browser-title">
            <span className="title-icon">📁</span>
            <h2>Browse Projects</h2>
            <span className="project-count">{projects.length} total</span>
          </div>
          <button className="close-browser-btn" onClick={onClose}>✕</button>
        </div>

        <div className="project-browser-search">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by project name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>
            )}
          </div>
          
          <div className="filter-tabs">
            <button className={`filter-tab ${selectedFilter === 'all' ? 'active' : ''}`} onClick={() => setSelectedFilter('all')}>
              All ({projects.length})
            </button>
            <button className={`filter-tab ${selectedFilter === 'active' ? 'active' : ''}`} onClick={() => setSelectedFilter('active')}>
              Active ({projects.filter(p => p.status === 'active').length})
            </button>
            <button className={`filter-tab ${selectedFilter === 'completed' ? 'active' : ''}`} onClick={() => setSelectedFilter('completed')}>
              Completed ({projects.filter(p => p.status === 'completed').length})
            </button>
          </div>

          <div className="view-toggle">
            <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>📱 Grid</button>
            <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>📋 List</button>
          </div>
        </div>

        {/* Recent Projects Section */}
        {recentProjects.length > 0 && searchTerm.length === 0 && selectedFilter === 'all' && currentPage === 1 && (
          <div className="recent-projects-section">
            <div className="section-header">
              <span className="section-icon">⏰</span>
              <h3>Recently Viewed</h3>
            </div>
            <div className="recent-projects-grid">
              {recentProjects.map(project => {
                const fullProject = projects.find(p => p.id === project.id);
                if (!fullProject) return null;
                return (
                  <div key={project.id} className="recent-project-card" onClick={() => handleSelectProject(fullProject)}>
                    <div className="recent-project-name">{project.name}</div>
                    <div className="recent-project-progress">{project.progress || 0}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="projects-browser-content">
          {currentProjects.length === 0 ? (
            <div className="no-projects-found">
              <div className="no-projects-icon">📭</div>
              <h3>No projects found</h3>
              <p>Try a different search term</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="projects-grid">
              {currentProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`project-card ${currentProjectId === project.id ? 'selected' : ''}`}
                  onClick={() => handleSelectProject(project)}
                >
                  <div className="project-card-header">
                    <div className="project-status-dot" style={{ background: getStatusColor(project.status) }}></div>
                    <span className="project-status">{project.status || 'active'}</span>
                  </div>
                  <div className="project-card-title">{project.projectName}</div>
                  <div className="project-card-progress">
                    <div className="progress-bar-small">
                      <div className="progress-fill-small" style={{ width: `${project.progress || 0}%` }}></div>
                    </div>
                    <span className="progress-percent">{project.progress || 0}%</span>
                  </div>
                  <div className="project-card-footer">
                    <span>📋 {project.totalTasks || 0} tasks</span>
                    <span>📅 {project.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}</span>
                  </div>
                  {currentProjectId === project.id && (
                    <div className="selected-badge">✓ Current</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="projects-list">
              {currentProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`project-list-item ${currentProjectId === project.id ? 'selected' : ''}`}
                  onClick={() => handleSelectProject(project)}
                >
                  <div className="list-item-info">
                    <div className="list-item-name">{project.projectName}</div>
                    <div className="list-item-meta">
                      <span className="list-item-status" style={{ color: getStatusColor(project.status) }}>{project.status || 'active'}</span>
                      <span className="list-item-progress">{project.progress || 0}% complete</span>
                      <span className="list-item-tasks">{project.totalTasks || 0} tasks</span>
                    </div>
                  </div>
                  <div className="list-item-progress-bar">
                    <div className="progress-bar-small">
                      <div className="progress-fill-small" style={{ width: `${project.progress || 0}%` }}></div>
                    </div>
                  </div>
                  <div className="list-item-date">
                    📅 {project.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                  </div>
                  {currentProjectId === project.id && (
                    <div className="selected-indicator">✓</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {startIndex + 1} - {Math.min(endIndex, totalProjects)} of {totalProjects} projects
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" onClick={() => goToPage(1)} disabled={currentPage === 1}>⟪</button>
              <button className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>←</button>
              <span className="pagination-current">Page {currentPage} of {totalPages}</span>
              <button className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>→</button>
              <button className="pagination-btn" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>⟫</button>
            </div>
          </div>
        )}

        {/* <div className="project-browser-footer">
          <button className="create-new-project-btn" onClick={() => {
            onClose();
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openCreateProjectModal'));
            }, 100);
          }}>
            + Create New Project
          </button>
          <button className="cancel-browser-btn" onClick={onClose}>Cancel</button>
        </div> */}
      </div>
    </div>
  );
};

export default ProjectBrowserModal;